import { useCallback } from "react";
import { listWorkflows, type WorkflowSummary } from "../../../api/content/workflowsApi";
import type { ThreadEntry, ThreadStartedPayload } from "../../../api/threads/liveMessages";
import { useLoad } from "../../../shared/loading/useLoad";
import { parseCollectionVersionRef } from "../../../shared/references/contentReferences";
import { useThreadContext } from "../page/threadPageContext";
import EntryFrame from "./EntryFrame";
import { ThreadStartedBody } from "./LogEventEntries";
import LongTextFold from "./LongTextFold";
import RunMarkdown from "./RunMarkdown";
import { startInputsDeclared, startTextInputs } from "./startInputs";

/**
 * A Run's first entry. What the starter wrote into the start form reads as their message, each text under its label
 * (Johannes, 4 Nov 15:36), the Workflow's own inputs and those the form asked for a later step; a start nobody can be
 * named for shows no name. A start without such text stays a line. A fork onto a newer content version keeps its
 * parent's start, whose Workflow version the Run's own listing may not hold: its labels are read at the start's own
 * content version (the 16:32 list, item 3).
 */
export default function StartedEntry({ entry, payload, author }: { entry: ThreadEntry; payload: ThreadStartedPayload; author: string }) {
  const { overview, workflowInputsOf, stepInputIn } = useThreadContext();
  const workflow = payload.workflow;
  const listedForRun = workflow === null ? null : workflowInputsOf(workflow);
  const startVersion = parseCollectionVersionRef(payload.bindings.content_version)?.commit ?? null;
  const runVersion = parseCollectionVersionRef(overview.bindings.content_version)?.commit ?? null;
  const readStartVersion = workflow !== null && listedForRun === null && startVersion !== null && startVersion !== runVersion;
  const listedForStart = useListedWorkflows(payload.project_id, readStartVersion ? startVersion : null);
  const declared = listedForRun ?? (workflow === null ? null : startInputsDeclared(listedForStart, workflow));
  const said = workflow === null ? [] : startTextInputs(payload.input_values, declared, (destination) => stepInputIn(workflow, destination));

  if (said.length === 0) {
    return (
      <EntryFrame entry={entry} authorLabel={author} layout="line">
        <ThreadStartedBody payload={payload} />
      </EntryFrame>
    );
  }
  return (
    <EntryFrame entry={entry} authorLabel={entry.author.kind === "user" ? author : ""} layout="card" tone="person">
      <p className="app-text-muted text-xs">
        <ThreadStartedBody payload={payload} />
      </p>
      {said.map((input) => (
        <div key={input.destination} className="space-y-1">
          <p className="app-text-muted text-xs font-semibold">{input.label}</p>
          <LongTextFold text={input.text} show={(text) => <RunMarkdown content={text} className="app-markdown text-sm" />} />
        </div>
      ))}
    </EntryFrame>
  );
}

/** A content version's listed Workflows, read only where `version` is given; `null` until read, or where it fails. */
function useListedWorkflows(projectId: string, version: string | null): readonly WorkflowSummary[] | null {
  const load = useCallback(
    (signal: AbortSignal) => (version === null ? Promise.resolve(null) : listWorkflows(projectId, version, signal).then((listing) => listing.workflows)),
    [projectId, version],
  );
  const { state } = useLoad(load);
  return state.status === "loaded" ? state.value : null;
}
